/**
 * PlaybookModal Component
 *
 * Modal for creating new playbooks within a strategy.
 * Handles two-write transaction: create playbook + update strategy.playbookIds.
 */

// 1. External dependencies
import React, { useState, useEffect } from 'react';

// 2. Internal absolute imports
import {
  Modal,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Button,
  Input,
  TextArea,
  Alert,
  useToastNotification,
} from '@enfomentis/ui';

// 3. Services & Types
import { db } from '@/lib/db';
import { playbookService } from '@/services/PlaybookService';
import { strategyService } from '@/services/StrategyService';
import { syncCoordinatorService } from '@/services/sync/SyncCoordinatorService';

/**
 * Props for PlaybookModal
 */
export interface PlaybookModalProps {
  /** Whether the modal is open */
  isOpen: boolean;
  /** Callback when modal closes */
  onClose: () => void;
  /** Callback after successful save - receives new playbook ID */
  onSave: (newPlaybookId?: string) => void;
  /** Strategy ID to link the playbook to (required) */
  strategyId: string;
  /** Strategy name for display */
  strategyName: string;
}

/**
 * Form data for playbook modal
 */
interface PlaybookFormData {
  name: string;
  description: string;
  philosophy: string;
}

/**
 * Initial empty form state
 */
const initialFormData: PlaybookFormData = {
  name: '',
  description: '',
  philosophy: '',
};

/**
 * PlaybookModal Component
 */
export const PlaybookModal: React.FC<PlaybookModalProps> = ({
  isOpen,
  onClose,
  onSave,
  strategyId,
  strategyName,
}) => {
  // Form state
  const [formData, setFormData] = useState<PlaybookFormData>(initialFormData);
  const [nameError, setNameError] = useState<string | null>(null);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Toast notifications
  const { showToast } = useToastNotification();

  // Reset form when modal opens
  useEffect(() => {
    if (isOpen) {
      setFormData(initialFormData);
      setNameError(null);
      setSubmitError(null);
    }
  }, [isOpen]);

  /**
   * Validate form data
   */
  const validateForm = (): boolean => {
    const trimmedName = formData.name.trim();
    if (!trimmedName) {
      setNameError('Name is required');
      return false;
    }
    setNameError(null);
    return true;
  };

  /**
   * Handle form field changes
   */
  const handleFieldChange = (field: keyof PlaybookFormData, value: string) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    // Clear error when field is edited
    if (field === 'name' && nameError) {
      setNameError(null);
    }
  };

  /**
   * Handle form submission
   * Uses Dexie transaction to ensure atomic create + link operation
   */
  const handleSubmit = async () => {
    setSubmitError(null);

    if (!validateForm()) {
      return;
    }

    try {
      setIsSubmitting(true);

      const trimmedName = formData.name.trim();
      const trimmedDescription = formData.description.trim();
      const trimmedPhilosophy = formData.philosophy.trim();

      // Build create payload
      const createData: Parameters<typeof playbookService.add>[0] = {
        name: trimmedName,
        entryModelIds: [], // Required field, start empty
      };

      // Only include optional fields if non-empty
      if (trimmedDescription) {
        createData.description = trimmedDescription;
      }
      if (trimmedPhilosophy) {
        createData.philosophy = trimmedPhilosophy;
      }

      let newPlaybookId: string | undefined;

      // Use Dexie transaction for atomic operation
      // Must include sync_operations and sync_state so nested service transactions can join
      await db.transaction('rw', [db.playbooks, db.strategies, db.sync_operations, db.sync_state], async () => {
        // 1. Create the playbook
        const newPlaybook = await playbookService.add(createData);
        newPlaybookId = newPlaybook.id;

        // 2. Get current strategy to read existing playbookIds
        const strategy = await strategyService.get(strategyId);
        if (!strategy) {
          throw new Error('Strategy not found');
        }

        // 3. Update strategy with new playbook ID appended
        const updatedPlaybookIds = [...(strategy.playbookIds || []), newPlaybook.id];
        await strategyService.update(strategyId, { playbookIds: updatedPlaybookIds });
      });

      // Notify sync for both tables (after transaction commits)
      if (newPlaybookId) {
        syncCoordinatorService.notifyChange('playbooks', newPlaybookId);
        syncCoordinatorService.notifyChange('strategies', strategyId);
      }

      showToast({
        title: 'Playbook Created',
        description: `"${trimmedName}" added to ${strategyName}`,
        color: 'success',
      });

      onSave(newPlaybookId);
      onClose();
    } catch (error) {
      console.error('Failed to create playbook:', error);
      const errorMessage = error instanceof Error ? error.message : 'Failed to create playbook';
      setSubmitError(errorMessage);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      size="md"
      placement="top-center"
    >
      <ModalContent>
        <ModalHeader>
          <div className="flex flex-col">
            <span>New Playbook</span>
            <span className="text-sm font-normal text-text-secondary">
              Adding to: {strategyName}
            </span>
          </div>
        </ModalHeader>

        <ModalBody className="gap-4">
          {/* Name */}
          <Input
            label="Name"
            placeholder="Enter playbook name"
            value={formData.name}
            onValueChange={(value) => handleFieldChange('name', value)}
            isRequired
            isInvalid={!!nameError}
            errorMessage={nameError}
            autoFocus
            className="[&_input]:text-base"
          />

          {/* Description */}
          <TextArea
            label="Description"
            placeholder="Brief description of this playbook (optional)"
            value={formData.description}
            onValueChange={(value) => handleFieldChange('description', value)}
            minRows={2}
            maxRows={4}
            className="[&_textarea]:text-base"
          />

          {/* Philosophy */}
          <TextArea
            label="Philosophy"
            placeholder="Trading philosophy for this playbook (optional)"
            value={formData.philosophy}
            onValueChange={(value) => handleFieldChange('philosophy', value)}
            minRows={3}
            maxRows={6}
            className="[&_textarea]:text-base"
          />

          {/* Error Alert */}
          {submitError && (
            <Alert color="danger" variant="flat">
              {submitError}
            </Alert>
          )}
        </ModalBody>

        <ModalFooter>
          <Button variant="flat" onPress={onClose} isDisabled={isSubmitting}>
            Cancel
          </Button>
          <Button
            color="primary"
            onPress={handleSubmit}
            isLoading={isSubmitting}
          >
            Create Playbook
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};

export default PlaybookModal;
